import React, { useEffect, useState } from "react";
import { Table, Tag, Tooltip, Input, Modal, Tabs } from "antd";
import { SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getLogs } from "../api/constants";
import { formatDateTime } from "../utils/constants";

const { Search } = Input;
const { TabPane } = Tabs;

export default function LogViewer() {
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [data, setData] = useState([]);

  useEffect(() => {
    // Fetch log data from the API
    fetchLogData();
  }, []);

  const fetchLogData = async () => {
    // Fetch log data from the API
    try {
      const res = await getLogs();
      setData(res);
    } catch (error) {
      console.log("error", error);
    }
  };

  const columns = [
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (text) => (
        <span className="font-semibold">{text.split("\\").pop()}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text) => (
        <Tag
          color={
            text === "created" ? "green" : text === "updated" ? "blue" : "red"
          }
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Details",
      key: "details",
      render: (text, record) => (
        <Tooltip title="Click to view details" placement="topLeft">
          <InfoCircleOutlined
            className="text-blue-500 cursor-pointer"
            onClick={() => {
              setSelectedLog(record);
              setModalVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_, record) => record.user?.full_name,
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => formatDateTime(text),
    },
  ];

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      JSON.stringify(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const renderJsonDiff = (oldData, newData) => {
    const allKeys = [
      ...new Set([
        ...Object.keys(oldData || {}),
        ...Object.keys(newData || {}),
      ]),
    ];
    return allKeys.map((key) => {
      const oldValue = oldData?.[key];
      const newValue = newData?.[key];
      const hasChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);
      return (
        <div
          key={key}
          className={`mb-2 ${hasChanged ? "bg-yellow-100 p-2 rounded" : ""}`}
        >
          <strong>{key}:</strong>
          {hasChanged ? (
            <>
              <div className="text-red-500 line-through">
                {JSON.stringify(oldValue)}
              </div>
              <div className="text-green-500">{JSON.stringify(newValue)}</div>
            </>
          ) : (
            <span>{JSON.stringify(newValue)}</span>
          )}
        </div>
      );
    });
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">System Activity Log</h1>
      <p className="mb-4 text-gray-600">
        This log shows recent activities in the system, including changes to
        agents, infractions, and payments. Use the search box to filter the log
        entries. Click on the info icon to view detailed information about each
        log entry.
      </p>
      <Search
        placeholder="Search logs..."
        allowClear
        enterButton={<SearchOutlined />}
        size="large"
        onChange={(e) => setSearchText(e.target.value)}
        className="mb-4"
      />
      <Table
        columns={columns}
        dataSource={filteredData}
        className="shadow-lg rounded-lg overflow-hidden"
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} sur ${total} actions`,
        }}
      />
      <Modal
        title={`Log Details - ${selectedLog?.model.split("\\").pop()}`}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Tabs defaultActiveKey="1">
            <TabPane tab="New Data" key="1">
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
                {JSON.stringify(selectedLog.new_data, null, 2)}
              </pre>
            </TabPane>
            {selectedLog.old_data && (
              <TabPane tab="Old Data" key="2">
                <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
                  {JSON.stringify(selectedLog.old_data, null, 2)}
                </pre>
              </TabPane>
            )}
            {selectedLog.action === "updated" && (
              <TabPane tab="Changes" key="3">
                <div className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
                  {renderJsonDiff(selectedLog.old_data, selectedLog.new_data)}
                </div>
              </TabPane>
            )}
          </Tabs>
        )}
      </Modal>
    </div>
  );
}
